import { createObjectAddress, ensureNodeId, getNodeId, resolveInGraph, type GraphNode, type NeedleAddress } from "@needle-tools/addressing";

/**
 * THE object-identity vocabulary at the AI boundary (plans/operation-bridge.md 1h,
 * plans/one-address-vocabulary.md): tool arguments and results name elements with
 * the DOCUMENT's target triple — `ref` (a document reference) + `address` (the
 * element within it) — exactly what ops and overs carry. Session handles (three
 * uuids, hierarchy node ids) never cross this boundary: they mean nothing after a
 * reload, and a model replaying one gets a loud miss, not a plausible wrong match.
 *
 * Products without document addressing (the chrome extension on a host page,
 * unstamped scenes) mint REF-LESS graph addresses and resolve them down the
 * path/name rungs — the designed degradation, not a failure mode.
 *
 * Dependencies are structural (no editor import): the pwa passes the real
 * IObjectTargetService and IHierarchyService; tests pass stubs.
 */

/** the document target triple as the model sees it */
export interface AiTarget {
    /** document reference id — absent when the product has no document */
    readonly ref?: string;
    /** the element's address within the reference (or the graph, when ref-less) */
    readonly address?: NeedleAddress;
}

/** the slice of IObjectTargetService this seam needs */
export interface TargetResolverLike {
    targetFor(object: object): { ref: string; address?: NeedleAddress; pathPrefix?: string } | null;
    objectFor(target: { ref: string; address?: NeedleAddress }): object | null;
}

/** the slice of IHierarchyService this seam needs */
export interface HierarchyLike {
    readonly roots: ReadonlyArray<{ owner?: unknown }>;
    findAllByNeedleId(id: string): ReadonlyArray<{ owner?: unknown }>;
}

/**
 * The target a tool result should carry for a live object. Document resolution
 * first (the ONE resolver — provenance stamps, needle-id index); ref-less graph
 * address as the degradation. Null = not addressable at all (e.g. a bare root
 * with no document identity) — callers report that, never invent a session id.
 */
export function aiTargetFor(targets: TargetResolverLike | undefined, hierarchy: HierarchyLike, object: object): AiTarget | null {
    const resolved = targets?.targetFor(object);
    if (resolved) return { ref: resolved.ref, ...(resolved.address ? { address: resolved.address } : {}) };
    const root = containingRootOwner(hierarchy, object);
    if (!root || root === object) return null;
    const address = createObjectAddress(root as GraphNode, object as GraphNode);
    return address ? { address } : null;
}

/**
 * {@link aiTargetFor}, with the RESOURCE degradation on top: an orphan element
 * (a material/texture/geometry with no scene root — resources have no parent
 * walk) gets its element identity MINTED in place, the same rule the
 * editor-state context and resources_list follow: a pointing gesture enters
 * the addressed flow. Null only for frozen/hostile host objects.
 */
export function aiTargetForElement(targets: TargetResolverLike | undefined, hierarchy: HierarchyLike, object: object): AiTarget | null {
    const resolved = aiTargetFor(targets, hierarchy, object);
    if (resolved) return resolved;
    try {
        const duck = object as { isMaterial?: boolean; isTexture?: boolean; isBufferGeometry?: boolean; name?: string };
        const type = duck.isMaterial ? "material" : duck.isTexture ? "texture" : duck.isBufferGeometry ? "geometry" : undefined;
        const id = ensureNodeId(object as GraphNode);
        return { address: { ...(type ? { type } : {}), id, ...(duck.name ? { name: duck.name } : {}) } };
    } catch {
        return null; // frozen/hostile host object — the label alone must do
    }
}

/**
 * WHERE a resource hangs, for anchors whose own address cannot survive a reload.
 *
 * A geometry or material three.js made at runtime has no durable identity: its
 * address is `{type, id}` with an id minted into the live object, and next load
 * that id belongs to nothing — while the rest of the ladder (path, name) walks
 * NODES, which a resource is not. What IS durable is the place it hangs: "the
 * geometry of the mesh at Scene/Grid". The holder is a node, nodes resolve by
 * path, and the slot is read from it.
 */
export type AnchorVia = {
    /** the object that holds the resource — resolved through the full ladder */
    readonly holder: AiTarget;
    readonly slot: "geometry" | "material";
    /** which entry, for a multi-material mesh — reading slot 0 when the chat
     *  was about slot 1 is a wrong answer delivered confidently */
    readonly index?: number;
    /** a TEXTURE hangs one hop deeper — on a material property (`map`,
     *  `normalMap`, …), not on the node. Set = the slot names the material,
     *  this names the property the texture is read from. */
    readonly texture?: string;
};

/** the object at a `via` slot, or null when the holder came back without one */
function readSlot(holder: object, via: AnchorVia): object | null {
    const duck = holder as { geometry?: unknown; material?: unknown };
    let value = via.slot === "geometry" ? duck.geometry : duck.material;
    if (Array.isArray(value)) {
        value = via.index === undefined ? value[0] : value[via.index];
    }
    if (!value || typeof value !== "object") return null;
    if (via.texture) {
        const entry = (value as Record<string, unknown>)[via.texture];
        return entry && typeof entry === "object" ? entry as object : null;
    }
    return value as object;
}

/**
 * The scene object holding this resource, and in which slot — minted ONCE, when
 * a chat is anchored to it, so the anchor keeps a rung that outlives the
 * session. Null when nothing in the scene holds it (a resource the user opened
 * from the resources panel that no object uses).
 */
export function viaForResource(
    targets: TargetResolverLike | undefined,
    hierarchy: HierarchyLike,
    resource: object,
): AnchorVia | null {
    // The ROOT is known from the descent, so the holder's address is built
    // against it directly — `aiTargetFor`'s parent walk would be a second,
    // weaker way of asking a question we have already answered.
    const holderTarget = (root: object, node: object): AiTarget | null => {
        const resolved = targets?.targetFor(node);
        if (resolved) return { ref: resolved.ref, ...(resolved.address ? { address: resolved.address } : {}) };
        const address = createObjectAddress(root as GraphNode, node as GraphNode);
        return address ? { address } : null;
    };

    const seen = new Set<unknown>();
    const visit = (root: object, node: unknown): AnchorVia | null => {
        if (!node || typeof node !== "object" || seen.has(node)) return null;
        seen.add(node);
        const duck = node as { geometry?: unknown; material?: unknown; children?: readonly unknown[] };
        // a texture sits ON a material — name the property so the via can take
        // the second hop (own enumerable props only: three's materials keep
        // their texture slots as plain own fields)
        const textureOn = (material: unknown): string | null => {
            if (!material || typeof material !== "object") return null;
            for (const [key, value] of Object.entries(material)) {
                if (value === resource) return key;
            }
            return null;
        };
        const slotOf = (): { slot: "geometry" | "material"; index?: number; texture?: string } | null => {
            if (duck.geometry === resource) return { slot: "geometry" };
            if (duck.material === resource) return { slot: "material" };
            if (Array.isArray(duck.material)) {
                const index = duck.material.indexOf(resource);
                if (index >= 0) return { slot: "material", index };
            }
            if ((resource as { isTexture?: boolean }).isTexture === true) {
                if (Array.isArray(duck.material)) {
                    for (let index = 0; index < duck.material.length; index++) {
                        const texture = textureOn(duck.material[index]);
                        if (texture) return { slot: "material", index, texture };
                    }
                } else {
                    const texture = textureOn(duck.material);
                    if (texture) return { slot: "material", texture };
                }
            }
            return null;
        };
        const slot = slotOf();
        if (slot) {
            const holder = holderTarget(root, node as object);
            if (holder) return { holder, ...slot };
        }
        for (const child of duck.children ?? []) {
            const found = visit(root, child);
            if (found) return found;
        }
        return null;
    };
    for (const root of hierarchy.roots) {
        const owner = root.owner;
        if (!owner || typeof owner !== "object") continue;
        const found = visit(owner as object, owner);
        if (found) return found;
    }
    return null;
}

/**
 * The live object an anchor is ABOUT: its own address first — that names the
 * thing — then `via`, which names the place it hangs. Never a guess: when
 * neither lands, the caller says the anchor does not resolve.
 */
export function resolveAnchorSubject(
    targets: TargetResolverLike | undefined,
    hierarchy: HierarchyLike,
    anchor: { target: AiTarget; via?: AnchorVia },
): object | null {
    const direct = resolveAiTarget(targets, hierarchy, anchor.target);
    if (direct) return direct;
    return resolveAnchorViaSubject(targets, hierarchy, anchor);
}

/**
 * The via rung ALONE — the live instance at the holder's slot, ignoring the
 * anchor's own address. Needed where the two diverge: the document resolver
 * answers a material ref with the document-side (file) material, while the
 * on-screen registrations (inspector, resources panel) hold the live clone —
 * the UI looks up the via subject when the direct one is nowhere on screen.
 */
export function resolveAnchorViaSubject(
    targets: TargetResolverLike | undefined,
    hierarchy: HierarchyLike,
    anchor: { via?: AnchorVia },
): object | null {
    if (!anchor.via) return null;
    const holder = resolveAiTarget(targets, hierarchy, anchor.via.holder);
    return holder ? readSlot(holder, anchor.via) : null;
}

/**
 * A target from a tool argument back to the live object. Ref-carrying targets go
 * through the ONE resolver; ref-less ones take the needle-id index first, then
 * the graph ladder (anchor → id → path) over every hierarchy root.
 */
export function resolveAiTarget(targets: TargetResolverLike | undefined, hierarchy: HierarchyLike, target: AiTarget): object | null {
    if (target.ref && targets) {
        const hit = targets.objectFor({ ref: target.ref, address: target.address });
        if (hit) return hit;
    }
    if (!target.address) return null;
    if (target.address.id && !target.address.anchor) {
        // identity is the PAIR (ref, id): without a ref, several holders can
        // legitimately share the id (the same asset placed twice carries the
        // same baked ids). Picking one silently is the wrong-copy bug class —
        // FAIL LOUD and name the way out instead.
        const holders = hierarchy.findAllByNeedleId(target.address.id)
            .map(node => node.owner)
            .filter((owner): owner is object => !!owner && typeof owner === "object");
        if (holders.length > 1) {
            const refs = targets ? holders.map(h => targets.targetFor(h)?.ref).filter(Boolean) : [];
            throw new Error(`Ambiguous target: ${holders.length} objects hold id "${target.address.id}"${refs.length ? ` (refs: ${refs.join(", ")})` : ""} — pass a target WITH a ref (search again for a current one).`);
        }
        if (holders.length === 1) return holders[0];
    }
    for (const root of hierarchy.roots) {
        const owner = root.owner;
        if (!owner || typeof owner !== "object") continue;
        const hit = resolveInGraph(target.address, owner as GraphNode);
        if (hit) return hit as object;
    }
    return null;
}

/** Resources are not hierarchy nodes. Resolve their stamped IDs through the same
 * weak registry used by resources_list, after the document target resolver. */
export function resolveAiTargetOrResource(
    targets: TargetResolverLike | undefined,
    hierarchy: HierarchyLike,
    target: AiTarget,
    resources?: { entries(): Iterable<{ deref(): object | undefined }> },
): object | null {
    const resolved = resolveAiTarget(targets, hierarchy, target);
    if (resolved) return resolved;
    const id = target.address?.id;
    const type = target.address?.type;
    if (!id || (type !== "material" && type !== "texture" && type !== "geometry")) return null;
    for (const entry of resources?.entries() ?? []) {
        const resource = entry.deref();
        if (resource && getNodeId(resource as GraphNode) === id) return resource;
    }
    return null;
}

/** a target from a tool ARGUMENT — the object form, or its JSON-stringified
 * form (a model serializes objects); anything else is null, never a guess */
export function parseAiTarget(raw: unknown): AiTarget | null {
    if (typeof raw === "string") {
        try { raw = JSON.parse(raw); }
        catch { return null; }
    }
    // ONE target wrapped in an array: `property_find` takes `targets` (plural)
    // and `property_read` takes `target` (singular), and a model that mixes them
    // used to get "target did not resolve" — the message for "this names nothing
    // in the scene" — and gave up on a one-line edit (2026-08-26). Several
    // targets are still refused: picking the first would edit something the user
    // never named.
    if (Array.isArray(raw)) raw = raw.length === 1 ? raw[0] : null;
    if (!raw || typeof raw !== "object") return null;
    const candidate = raw as { ref?: unknown; address?: unknown };
    const ref = typeof candidate.ref === "string" && candidate.ref.length > 0 ? candidate.ref : undefined;
    const address = candidate.address && typeof candidate.address === "object" ? candidate.address as NeedleAddress : undefined;
    if (!ref && !address) return null;
    return { ...(ref ? { ref } : {}), ...(address ? { address } : {}) };
}

/** the hierarchy root whose graph contains the object (parent walk) */
function containingRootOwner(hierarchy: HierarchyLike, object: object): object | null {
    const rootOwners = new Set<object>();
    for (const root of hierarchy.roots) {
        if (root.owner && typeof root.owner === "object") rootOwners.add(root.owner);
    }
    for (let node: object | null | undefined = object; node; node = (node as { parent?: object | null }).parent) {
        if (rootOwners.has(node)) return node;
    }
    return null;
}
